import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Modal,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Grid, Stack, TablePagination
} from "@mui/material";
import http from "../../utils/http-common";
import { toast } from "react-toastify";
import { styled } from '@mui/material/styles';
import { Edit as EditIcon, ChangeCircle as ChangeCircleIcon } from '@mui/icons-material';
const StatusButton = styled(Box)(({ status }) => ({
  padding: '2px 8px',
  borderRadius: '20px',
  fontSize: '12px',
  fontWeight: 600,
  backgroundColor: 'transparent',
  border: `1px solid ${getStatusColor(status)}`,
  color: getStatusColor(status),
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '24px',
  minWidth: '90px',
  textTransform: 'capitalize'
}));

// Add this function before your component to get status color
const getStatusColor = (status) => {
  const statusColors = {
    'Open': '#2196F3',        // Blue
    'In Progress': '#FF9800', // Orange
    'Pending': '#FFA726',     // Light Orange
    'Requested': '#666666',   // Gray
    'Schedule': '#9C27B0',    // Purple
    'Technical': '#00ACC1',   // Cyan
    'Completed': '#4CAF50',   // Green
    'Blocked': '#F44336'      // Red
  };
  return statusColors[status] || '#666666'; // Default gray if status not found
};
export default function RiskRegister() {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  // Add pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const [riskModalOpen, setRiskModalOpen] = useState(false);

  const [riskData, setRiskData] = useState({
    id: "",
    risk_statement: "",
    risk_owner: "",
    probability: "",
    impact: "",
    risk_score: "",
    response_strategies: "",
    revised_probability: "",
    revised_impact: "",
    revised_risk_score: "",
    action: "",
    status: "Open",
    comment: ""
  });

  const [risks, setRisks] = useState([]);
  const planId = localStorage.getItem("plan_id");
  useEffect(() => {
    fetchPlanData();
  }, [planId]);
  const fetchPlanData = async () => {
    try {
      const response = await http.get(`/master_service/get_project/plan/summary/${planId}`);
      if (response.status === 200) {
        const data = response.data.risks.map(item => ({
          id: item.risk_id,
          risk_statement: item.risk_statement,
          risk_owner: item.risk_owner,
          probability: item.probability,
          impact: item.impact,
          risk_score: item.risk_score,
          response_strategies: item.response_strategies,
          revised_probability: item.revised_probability,
          revised_impact: item.revised_impact,
          revised_risk_score: item.revised_risk_score,
          action: item.action,
          status: item.status,
          comment: item.comment
        }));
        setRisks(data);
      }
    } catch (error) {
      console.error("Error fetching user data", error);
    }
  };

  const [filters, setFilters] = useState({
    issues: '',
    assumptions: '',
    risks: '',
    dependencies: '',
    constraints: ''
  });
  const filterRisks = () => {
    return risks.filter(risk =>
      Object.values(risk).some(value =>
        value ? value.toString().toLowerCase().includes(filters.risks.toLowerCase()) : false
      )
    );
  };

  const [expandedSections, setExpandedSections] = useState({});
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };
  const handleInputChange2 = (field, value) => {
    setRiskData(prev => ({ ...prev, [field]: value }));
  };
  const handleFilterChange = (table, value) => {
    setFilters(prev => ({
      ...prev,
      [table]: value
    }));
  };

  const handleEdit = (risk) => {
    setRiskData({
      id: risk.id,
      risk_statement: risk.risk_statement,
      risk_owner: risk.risk_owner,
      probability: risk.probability,
      impact: risk.impact,
      risk_score: risk.risk_score,
      revised_probability: risk.revised_probability,
      revised_impact: risk.revised_impact,
      revised_risk_score: risk.revised_risk_score,
      action: risk.action,
      response_strategies: risk.response_strategies,
      comment: risk.comment,
      status: risk.status,
      plan_id: planId
    });
    setRiskModalOpen(true);
  };
  const handleStatusUpdate = async (risk) => {

    const nextStatus = getNextStatus(risk.status);
    try {
      const payload = {
        risk_id: risk.id,
        risk_statement: risk.risk_statement,
        risk_owner: risk.risk_owner,
        probability: risk.probability,
        impact: risk.impact,
        risk_score: risk.risk_score,
        revised_probability: risk.revised_probability,
        revised_impact: risk.revised_impact,
        revised_risk_score: risk.revised_risk_score,
        action: risk.action,
        response_strategies: risk.response_strategies,
        comment: risk.comment,
        plan_id: planId,
        status: nextStatus,
      };
      const response = await http.post('/master_service/create_update/risk_register', payload);
      if (response.status === 200 || response.status === 201) {
        toast.success("Status updated successfully");
        fetchPlanData();
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Helper function to determine next status
  const getNextStatus = (currentStatus) => {
    const statusFlow = {
      'Open': 'In Progress',
      'In Progress': 'Completed',
      'Completed': 'Open',
      'Blocked': 'Open',
      'Technical': 'In Progress',
      'Pending': 'In Progress',
      'Requested': 'In Progress',
      'Schedule': 'In Progress'
    };
    return statusFlow[currentStatus] || 'Open';
  };

  const handleSave = async (data, closeModal, resetData) => {
    try {
      const payload = {
        risk_id: data.id,
        risk_statement: data.risk_statement,
        risk_owner: data.risk_owner,
        probability: data.probability,
        impact: data.impact,
        risk_score: data.risk_score,
        revised_probability: data.revised_probability,
        revised_impact: data.revised_impact,
        revised_risk_score: data.revised_risk_score,
        action: data.action,
        response_strategies: data.response_strategies,
        comment: data.comment,
        plan_id: planId,
        "status": data.status
      };
      const response = await http.post('/master_service/create_update/risk_register', payload);
      if (response.status === 201 || response.status === 200) {
        toast.success("Data saved successfully");
        closeModal(false);
        resetData();
        fetchPlanData();
      }
    } catch (error) {
      toast.error("Failed to save data");
    }
  };

  return (
    <Container maxWidth={false} sx={{ width: "100%", p: 1 }}>

      {/* Risk Register Table */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#F7F8F8', p: 0.5, mb: 0.2 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
          <Typography style={{ fontWeight: "600" }}>Risk Register</Typography>
          <Typography sx={{ ml: 1, fontSize: '12px' }}>({risks.length} Risks)</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>

          <TextField
            size="small"
            placeholder="Search here..."
            value={filters.risks}
            onChange={(e) => handleFilterChange('risks', e.target.value)}
            sx={{ ml: 2, mr: 1, minWidth: '200px' }}
          />
          <Button
            onClick={() => setRiskModalOpen(true)}
            sx={{
              minWidth: '24px',
              width: '26px',
              height: '26px',
              p: 0,
              mr: '4px',
              color: '#FFF',
              border: 'none',
              backgroundColor: '#FF2800',
              '&:hover': {
                backgroundColor: '#FF2800',
                border: 'none'
              }
            }}
          >
            +
          </Button>
        </Box>
      </Box>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#e0eaff" }}>
            <TableRow sx={{ '& .MuiTableCell-head': { py: 0.1, fontSize: '12px', fontWeight: 500 } }}>
              <TableCell>Risk ID</TableCell>
              <TableCell>Risk Statement</TableCell>
              <TableCell>Risk Owner</TableCell>
              <TableCell>Probability</TableCell>
              <TableCell>Impact</TableCell>
              <TableCell>Risk Score</TableCell>
              <TableCell>Response Strategies</TableCell>
              <TableCell>Revised Probability</TableCell>
              <TableCell>Revised Impact</TableCell>
              <TableCell>Revised Score</TableCell>
              <TableCell>Actions</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Comments</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filterRisks().slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((risk, index) => (
                <TableRow key={index} sx={{ '& .MuiTableCell-body': { py: 0.2 } }}>
                  <TableCell>{risk.id}</TableCell>
                  <TableCell>{risk.risk_statement}</TableCell>
                  <TableCell>{risk.risk_owner}</TableCell>
                  <TableCell>{risk.probability}</TableCell>
                  <TableCell>{risk.impact}</TableCell>
                  <TableCell>{risk.risk_score}</TableCell>
                  <TableCell>{risk.response_strategies}</TableCell>
                  <TableCell>{risk.revised_probability}</TableCell>
                  <TableCell>{risk.revised_impact}</TableCell>
                  <TableCell>{risk.revised_risk_score}</TableCell>
                  <TableCell>{risk.action}</TableCell>
                  <TableCell>{risk.comment}</TableCell>                  
                  <TableCell><StatusButton status={risk.status}>{risk.status}</StatusButton></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(risk)}
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon sx={{ height: "18px", width: "18px", color: "#666" }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleStatusUpdate(risk)}
                        sx={{ p: 0.5 }}
                      >
                        <ChangeCircleIcon sx={{ height: "18px", width: "18px", color: getStatusColor(risk.status) }} />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        {filterRisks().length > 10 && (
          <TablePagination
            rowsPerPageOptions={[10]}
            component="div"
            count={filterRisks().length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            sx={{
              '.MuiTablePagination-toolbar': {
                minHeight: '40px',
              },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: '12px',
              }
            }}
          />
        )}
      </TableContainer>
      {/* Risk Modal */}
      <Modal
        open={riskModalOpen}
        onClose={() => setRiskModalOpen(false)}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-end"
        }}
      >
        <Box
          sx={{
            width: "350px",
            height: "100%",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 1,
            overflowY: "auto",
            '& .MuiInputBase-input': { // Add this style to reduce input font size
              fontSize: '0.875rem'
            },
            '& .MuiInputLabel-root': { // Add this style to reduce label font size
              fontSize: '0.875rem'
            },
            '& .MuiMenuItem-root': { // Add this style to reduce dropdown item font size
              fontSize: '0.875rem'
            }
          }}
        >
          <Typography mb={1.5} style={{ font: 'bold', fontWeight: '600' }}>Add Risk</Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Risk Statement"
            value={riskData.risk_statement}
            onChange={(e) => handleInputChange2("risk_statement", e.target.value)}
            sx={{ mb: 1.5 }}
            size="small"
            inputProps={{ maxLength: 200 }}
          />

          <TextField
            fullWidth
            label="Risk Owner"
            value={riskData.risk_owner}
            onChange={(e) => {
              const newValue = e.target.value;
              handleInputChange2("risk_owner", newValue);
            }}
            sx={{ mb: 1.5 }}
            size="small"
          />

          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel id="probability-label" shrink="true">Probability</InputLabel>
            <Select
              value={riskData.probability}
              labelId="probability-label"
              label="Probability"
              onChange={(e) => handleInputChange2("probability", e.target.value)}
              size="small"
              notched
            >
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel id="impact-label" shrink="true">Impact</InputLabel>
            <Select
              value={riskData.impact}
              labelId="impact-label"
              label="Impact"
              onChange={(e) => handleInputChange2("impact", e.target.value)}
              size="small"
              notched
            >
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
              <MenuItem value="Very High">Very High</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Risk Score"
            value={riskData.risk_score}
            onChange={(e) => 
              {
                const newValue = e.target.value.replace(/[^0-9]/g, "");
                handleInputChange2("risk_score", newValue)}}
            sx={{ mb: 1.5 }}
            size="small"
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Response Strategies"
            value={riskData.response_strategies}
            onChange={(e) => handleInputChange2("response_strategies", e.target.value)}
            sx={{ mb: 1.5 }}
            size="small"
            inputProps={{ maxLength: 200 }}
          />

          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel id="revised-probability" shrink="true">Revised Probability</InputLabel>
            <Select
              value={riskData.revised_probability}
              labelId="revised-probability"
              label="Revised Probability"
              onChange={(e) => handleInputChange2("revised_probability", e.target.value)}
              size="small"
              notched
            >
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel id="revised-impact" shrink="true">Revised Impact</InputLabel>
            <Select
              value={riskData.revised_impact}
              labelId="revised-impact"
              label="Revised Impact"
              onChange={(e) => handleInputChange2("revised_impact", e.target.value)}
              size="small"
              notched
            >
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
              <MenuItem value="Very High">Very High</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Revised Score"
            value={riskData.revised_risk_score}
            onChange={(e) => 
                            {
                const newValue = e.target.value.replace(/[^0-9]/g, "");
              handleInputChange2("revised_risk_score", newValue);
            }}
            sx={{ mb: 1.5 }}
            size="small"
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Actions"
            value={riskData.action}
            onChange={(e) => handleInputChange2("action", e.target.value)}
            sx={{ mb: 1.5 }}
            size="small"
            inputProps={{ maxLength: 100 }}
          />
          <FormControl fullWidth sx={{ mb: 1.5 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={riskData.status}
              label="Status"
              onChange={(e) => handleInputChange2("status", e.target.value)}
              size="small"
            >
              <MenuItem value="Open">Open</MenuItem>
              <MenuItem value="In-Progress">In-Progress</MenuItem>
              <MenuItem value="Closed">Closed</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="comment"
            value={riskData.comment}
            onChange={(e) => handleInputChange2("comment", e.target.value)}
            sx={{ mb: 1.5 }}
            size="small"
            inputProps={{ maxLength: 100 }}
          />
          <Button
            fullWidth
            onClick={() => handleSave(riskData, setRiskModalOpen, () => setRiskData({
              id: "",
              risk_statement: "",
              risk_owner: "",
              probability: "",
              impact: "",
              risk_score: "",
              response_strategies: "",
              revised_probability: "",
              revised_impact: "",
              revised_risk_score: ""
            }))}
            sx={{
              textTransform: "none",
              bgcolor: '#FF2800',
              color: 'white',
              mb: 0.5,
              "&:hover": { bgcolor: "#FF2800" }
            }}
          >
            Save
          </Button>

          <Button
            fullWidth
            onClick={() => setRiskModalOpen(false)}
            sx={{ textTransform: "none", bgcolor: 'white', color: 'black' }}
          >
            Cancel
          </Button>
        </Box>
      </Modal>
    </Container>
  )
}